import {
  adminStoreSchema,
  type ClaimStatus,
  type ClaimUpdate,
  claimViewSchema,
  jobPhotosResponseSchema,
  peopleResponseSchema,
  type PersonInput,
  personSchema,
  reportsResponseSchema,
  type StoreInput,
  storesResponseSchema,
  listJobsResponseSchema,
  meResponseSchema,
  photoUploadResponseSchema,
  vinDecodeResponseSchema,
  type JobStatus,
  type PhotoKind,
  type SubmitJobInput,
  type SyncTarget, opsQueueResponseSchema } from "@agw/contracts";
import { z } from "zod";
import { ApiError, request } from "./api";

// One function per route in ROUTES (packages/contracts/src/api.ts).

/** Job routes may return the view bare or wrapped as { job }. Accept both, return the view. */
const jobResultSchema = z
  .union([z.object({ job: claimViewSchema, created: z.boolean().optional() }), claimViewSchema])
  .transform((v) => ("job" in v && typeof v.job === "object" ? v.job : (v as z.infer<typeof claimViewSchema>)));

const jobListSchema = z
  .union([listJobsResponseSchema, z.array(claimViewSchema)])
  .transform((v) => (Array.isArray(v) ? { items: v, nextCursor: undefined } : v));

export const importResultSchema = z.looseObject({
  updated: z.number().optional(),
  unchanged: z.number().optional(),
  unknownJobNumbers: z.array(z.string()).optional(),
  rejected: z.array(z.object({ glassBillerJobNumber: z.string(), reason: z.string() })).optional(),
});
export type ImportResultView = z.infer<typeof importResultSchema>;

export const api = {
  requestCode: (login: string) => request("POST", "/api/auth/code", { body: { login } }),
  verifyCode: (login: string, code: string) => request("POST", "/api/auth/verify", { body: { login, code }, schema: meResponseSchema }),
  logout: () => request("POST", "/api/auth/logout"),

  /** Resolves to null when there is no session. */
  me: async () => {
    try {
      return await request("GET", "/api/me", { schema: meResponseSchema });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },

  createPhotoUpload: (body: { contentType: string; sizeBytes: number; kind: PhotoKind; thumbSizeBytes?: number }) =>
    request("POST", "/api/photos/uploads", { body, schema: photoUploadResponseSchema }),

  decodeVin: (vin: string, signal?: AbortSignal) =>
    request("GET", `/api/vin/${encodeURIComponent(vin)}`, { schema: vinDecodeResponseSchema, signal }),

  submitJob: (body: SubmitJobInput) => request("POST", "/api/jobs", { body, schema: jobResultSchema }),

  listJobs: (q: { storeId?: string; status?: JobStatus; claimStatus?: ClaimStatus; repId?: string; cursor?: string; limit?: number }) =>
    request("GET", "/api/jobs", { query: q, schema: jobListSchema }),

  getJob: (jobId: string) => request("GET", `/api/jobs/${encodeURIComponent(jobId)}`, { schema: jobResultSchema }),

  opsQueue: () => request("GET", "/api/ops/queue", { schema: opsQueueResponseSchema }),

  recordGlassBillerNumber: (jobId: string, glassBillerJobNumber: string) =>
    request("POST", `/api/ops/jobs/${encodeURIComponent(jobId)}/glassbiller-number`, { body: { glassBillerJobNumber }, schema: jobResultSchema }),

  retrySync: (jobId: string, target: SyncTarget) =>
    request("POST", `/api/ops/jobs/${encodeURIComponent(jobId)}/retry-sync`, { body: { target }, schema: jobResultSchema }),

  /** Rows are parsed from the GlassBiller CSV in the browser and sent as JSON. */
  importGlassBiller: (rows: unknown[]) => request("POST", "/api/ops/glassbiller-import", { body: { rows }, schema: importResultSchema }),

  opsFailed: () => request("GET", "/api/ops/failed", { schema: listJobsResponseSchema }),

  jobPhotos: (jobId: string) => request("GET", `/api/jobs/${encodeURIComponent(jobId)}/photos`, { schema: jobPhotosResponseSchema }),

  updateClaim: (jobId: string, body: ClaimUpdate) =>
    request("PATCH", `/api/ops/jobs/${encodeURIComponent(jobId)}/claim`, { body, schema: jobResultSchema }),

  reports: (from: string, to: string) => request("GET", "/api/reports", { query: { from, to }, schema: reportsResponseSchema }),

  people: () => request("GET", "/api/admin/people", { schema: peopleResponseSchema }),
  createPerson: (body: PersonInput) => request("POST", "/api/admin/people", { body, schema: personSchema }),
  updatePerson: (userId: string, body: PersonInput) =>
    request("PUT", `/api/admin/people/${encodeURIComponent(userId)}`, { body, schema: personSchema }),
  setTeam: (managerId: string, repIds: string[]) =>
    request("PUT", `/api/admin/people/${encodeURIComponent(managerId)}/team`, { body: { repIds }, schema: peopleResponseSchema }),

  stores: () => request("GET", "/api/admin/stores", { schema: storesResponseSchema }),
  createStore: (body: StoreInput) => request("POST", "/api/admin/stores", { body, schema: adminStoreSchema }),
  updateStore: (storeId: string, body: StoreInput) =>
    request("PUT", `/api/admin/stores/${encodeURIComponent(storeId)}`, { body, schema: adminStoreSchema }),
};

export type Api = typeof api;
